import { Minus, Plus, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import {
  ApiError,
  providerApi,
  type FoodItemRecord,
  type PaymentMethod,
  type ProviderFoodOrder,
  type ProviderSession,
  type VenueSettingsRecord,
} from "../../lib/api";
import { markLocalActivity } from "../../lib/activity-alerts";
import { useAppPreferences } from "../../lib/preferences";
import { formatEgp } from "./order-status";
import { stockLabel } from "./useVenueMenu";

interface OrderComposerProps {
  /** Active sessions the order can go to. With one session the picker is hidden. */
  sessions: ProviderSession[];
  initialSessionId?: string | null;
  menu: FoodItemRecord[];
  menuLoading?: boolean;
  menuError?: string | null;
  onPlaced: (order: ProviderFoodOrder, receiptNumber?: string) => void;
  onCancel?: () => void;
  onOpenMenuSetup?: () => void;
  /** Offer "Counter (pay now)" alongside the tables; needs the venue's charging settings. */
  counter?: { venueId: string; settings: VenueSettingsRecord | null };
}

const COUNTER = "counter";

type Cart = Record<string, number>;

/** Round piastres half-up like the API's Decimal quantize; the epsilon absorbs float noise. */
function halfUpCents(cents: number) {
  return Math.floor(cents + 0.5 + 1e-7);
}

/**
 * Tap items to build an order for one table, then send it to the queue.
 * Stock is checked again by the server; its message is shown as-is.
 */
export function OrderComposer({
  sessions,
  initialSessionId,
  menu,
  menuLoading,
  menuError,
  onPlaced,
  onCancel,
  onOpenMenuSetup,
  counter,
}: OrderComposerProps) {
  const { language, t } = useAppPreferences();
  const [sessionId, setSessionId] = useState<string>(
    initialSessionId ?? (sessions.length === 1 && !counter ? sessions[0]!.id : sessions.length === 0 && counter ? COUNTER : ""),
  );
  const [guestName, setGuestName] = useState("");
  const [method, setMethod] = useState<PaymentMethod | null>(null);
  const [cashReceived, setCashReceived] = useState("");
  const [cart, setCart] = useState<Cart>({});
  const [note, setNote] = useState("");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string>("all");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onMenu = useMemo(() => menu.filter((item) => item.is_available), [menu]);
  const categories = useMemo(
    () => Array.from(new Set(onMenu.map((item) => item.category))).sort(),
    [onMenu],
  );
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return onMenu.filter((item) =>
      (category === "all" || item.category === category)
      && (!needle || item.name.toLowerCase().includes(needle)));
  }, [category, onMenu, query]);

  const lines = Object.entries(cart)
    .map(([itemId, quantity]) => ({ item: menu.find((entry) => entry.id === itemId), quantity }))
    .filter((line): line is { item: FoodItemRecord; quantity: number } => Boolean(line.item) && line.quantity > 0);
  const total = lines.reduce((sum, line) => sum + line.item.price * line.quantity, 0);
  const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);
  const selectedSession = sessions.find((session) => session.id === sessionId) ?? null;
  const isCounter = Boolean(counter) && sessionId === COUNTER;
  // Counter sales are charged now, so show the same service fee and VAT the receipt will.
  const settings = counter?.settings ?? null;
  const subtotalCents = lines.reduce((sum, line) => sum + halfUpCents(line.item.price * 100) * line.quantity, 0);
  const serviceCents = settings?.service_fee_enabled ? halfUpCents(subtotalCents * settings.service_fee_percent / 100) : 0;
  const vatCents = settings?.vat_enabled ? halfUpCents((subtotalCents + serviceCents) * settings.vat_percent / 100) : 0;
  const serviceFee = serviceCents / 100;
  const vat = vatCents / 100;
  const counterTotal = (subtotalCents + serviceCents + vatCents) / 100;
  const cashCents = Math.round(Number(cashReceived) * 100);
  const changeDue = method === "cash" && Number.isFinite(cashCents) ? Math.max(0, cashCents - Math.round(counterTotal * 100)) / 100 : 0;
  const cashShort = method === "cash" && cashReceived.trim() !== "" && cashCents < Math.round(counterTotal * 100);
  const quickCash = Array.from(new Set([counterTotal, ...[50, 100, 200, 500].map((step) => Math.ceil(counterTotal / step) * step)]))
    .filter((value) => value >= counterTotal && value > 0)
    .slice(0, 4);

  const maxFor = (item: FoodItemRecord) => (item.track_stock ? item.stock_quantity ?? 0 : 99);

  const add = (item: FoodItemRecord) => {
    setError(null);
    setCart((current) => {
      const next = Math.min((current[item.id] ?? 0) + 1, maxFor(item));
      return { ...current, [item.id]: next };
    });
  };

  const setQuantity = (itemId: string, quantity: number) => {
    setCart((current) => {
      const next = { ...current };
      if (quantity <= 0) delete next[itemId];
      else next[itemId] = quantity;
      return next;
    });
  };

  const send = async () => {
    if (!selectedSession && !isCounter) {
      setError(t("Choose who the order is for."));
      return;
    }
    if (lines.length === 0) {
      setError(t("Add at least one item."));
      return;
    }
    if (isCounter && !method) {
      setError(t("Choose how they're paying."));
      return;
    }
    if (isCounter && cashShort) {
      setError(t("The cash received is less than the total."));
      return;
    }
    setSending(true);
    setError(null);
    try {
      if (isCounter && counter && method) {
        const result = await providerApi.createCounterSale({
          venue_id: counter.venueId,
          items: lines.map((line) => ({ food_item_id: line.item.id, quantity: line.quantity })),
          guest_name: guestName.trim() || null,
          notes: note.trim() || null,
          payment_method: method,
          cash_received_amount: method === "cash" && cashReceived.trim() ? Number(cashReceived) : null,
        });
        markLocalActivity(result.order.id);
        setCart({});
        setNote("");
        setGuestName("");
        setCashReceived("");
        onPlaced(result.order, result.receipt.receipt_number);
        return;
      }
      if (!selectedSession) return;
      const order = await providerApi.createFoodOrder({
        session_id: selectedSession.id,
        items: lines.map((line) => ({ food_item_id: line.item.id, quantity: line.quantity })),
        notes: note.trim() || null,
      });
      markLocalActivity(order.id);
      setCart({});
      setNote("");
      onPlaced(order);
    } catch (requestError) {
      setError(requestError instanceof ApiError && requestError.status !== 0
        ? requestError.detail
        : t("Could not send the order. Check your connection and try again."));
    } finally {
      setSending(false);
    }
  };

  if (sessions.length === 0 && !counter) {
    return (
      <div className="ops-empty grid gap-2 py-6">
        <p>{t("No one is playing right now.")}</p>
        <p className="ops-copy text-xs">{t("Orders are added to a table's bill, so start a session on the Floor first.")}</p>
      </div>
    );
  }

  return (
    <div className="order-composer">
      <div className="order-composer-menu">
        {sessions.length > 1 || !initialSessionId || counter ? (
          <fieldset className="grid gap-2">
            <legend className="ops-label mb-1">{t("Order for")}</legend>
            <div className="order-composer-tables">
              {counter ? (
                <button
                  aria-pressed={isCounter}
                  className="order-composer-table"
                  data-counter="true"
                  data-selected={isCounter ? "true" : undefined}
                  onClick={() => { setSessionId(COUNTER); setError(null); }}
                  type="button"
                >
                  <span className="font-semibold">{t("Counter")}</span>
                  <span className="ops-copy text-xs">{t("Pay now, no table")}</span>
                </button>
              ) : null}
              {sessions.map((session) => (
                <button
                  aria-pressed={session.id === sessionId}
                  className="order-composer-table"
                  data-selected={session.id === sessionId ? "true" : undefined}
                  key={session.id}
                  onClick={() => { setSessionId(session.id); setError(null); }}
                  type="button"
                >
                  <span className="font-semibold">{session.unit_name}</span>
                  <span className="ops-copy text-xs">{session.customer_name}</span>
                </button>
              ))}
            </div>
          </fieldset>
        ) : null}

        <div className="grid gap-2">
          <label className="order-composer-search">
            <Search aria-hidden="true" size={16} />
            <span className="sr-only">{t("Search the menu")}</span>
            <input
              className="ops-input"
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("Search the menu")}
              type="search"
              value={query}
            />
          </label>
          {categories.length > 1 ? (
            <div className="flex flex-wrap gap-1.5" role="group" aria-label={t("Category")}>
              {["all", ...categories].map((entry) => (
                <button
                  aria-pressed={category === entry}
                  className="ops-button"
                  data-size="sm"
                  data-variant={category === entry ? "primary" : "secondary"}
                  key={entry}
                  onClick={() => setCategory(entry)}
                  type="button"
                >
                  {entry === "all" ? t("All") : t(entry)}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {menuError ? <div className="ops-banner" data-tone="danger" role="alert">{menuError}</div> : null}
        {menuLoading && menu.length === 0 ? <div className="ops-empty py-4">{t("Loading menu…")}</div> : null}
        {!menuLoading && onMenu.length === 0 && !menuError ? (
          <div className="ops-empty grid gap-2 py-4">
            <p>{t("Nothing on the menu yet.")}</p>
            {onOpenMenuSetup ? (
              <button className="ops-button justify-self-center" data-size="sm" data-variant="secondary" onClick={onOpenMenuSetup} type="button">
                {t("Set up the menu")}
              </button>
            ) : null}
          </div>
        ) : null}

        <div className="order-composer-items">
          {visible.map((item) => {
            const inCart = cart[item.id] ?? 0;
            const soldOut = Boolean(item.sold_out);
            const atLimit = item.track_stock && inCart >= (item.stock_quantity ?? 0);
            const stock = stockLabel(item, t);
            return (
              <button
                className="order-composer-item"
                data-in-cart={inCart > 0 ? "true" : undefined}
                disabled={soldOut || atLimit}
                key={item.id}
                onClick={() => add(item)}
                type="button"
              >
                <span className="order-composer-item-name">{item.name}</span>
                <span className="order-composer-item-meta">
                  <span>{formatEgp(item.price, language)}</span>
                  {stock ? (
                    <span data-tone={soldOut ? "danger" : item.low_stock ? "warning" : undefined}>{stock}</span>
                  ) : null}
                </span>
                {inCart > 0 ? <span className="order-composer-item-count" aria-label={t("{count} in order", { count: inCart })}>{inCart}</span> : null}
              </button>
            );
          })}
        </div>
      </div>

      <div className="order-composer-cart" aria-live="polite">
        <div className="flex items-baseline justify-between gap-2">
          <p className="ops-kicker">{t("This order")}</p>
          {isCounter ? (
            <span className="ops-copy text-xs">{t("Counter sale")}</span>
          ) : selectedSession ? (
            <span className="ops-copy text-xs">{selectedSession.unit_name} · {selectedSession.customer_name}</span>
          ) : null}
        </div>

        {lines.length === 0 ? (
          <p className="ops-copy text-sm">{t("Tap items on the menu to add them.")}</p>
        ) : (
          <ul className="grid gap-2">
            {lines.map(({ item, quantity }) => (
              <li className="order-composer-line" key={item.id}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{item.name}</p>
                  <p className="ops-copy text-xs">{formatEgp(item.price * quantity, language)}</p>
                </div>
                <div className="order-composer-stepper">
                  <button
                    aria-label={quantity === 1 ? t("Remove {name}", { name: item.name }) : t("One less {name}", { name: item.name })}
                    className="ops-button"
                    data-size="sm"
                    data-variant="secondary"
                    onClick={() => setQuantity(item.id, quantity - 1)}
                    type="button"
                  >
                    {quantity === 1 ? <Trash2 size={14} /> : <Minus size={14} />}
                  </button>
                  <span className="order-composer-qty">{quantity}</span>
                  <button
                    aria-label={t("One more {name}", { name: item.name })}
                    className="ops-button"
                    data-size="sm"
                    data-variant="secondary"
                    disabled={quantity >= maxFor(item)}
                    onClick={() => setQuantity(item.id, quantity + 1)}
                    type="button"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <label className="grid gap-1">
          <span className="ops-label">{t("Note for the kitchen (optional)")}</span>
          <input
            className="ops-input"
            maxLength={500}
            onChange={(event) => setNote(event.target.value)}
            placeholder={t("No ice, extra spicy…")}
            value={note}
          />
        </label>

        {error ? <div className="ops-banner" data-tone="danger" role="alert">{error}</div> : null}

        {isCounter ? (
          <>
            <label className="grid gap-1">
              <span className="ops-label">{t("Name for the order (optional)")}</span>
              <input className="ops-input" maxLength={120} onChange={(event) => setGuestName(event.target.value)} placeholder={t("So the kitchen can call it out")} value={guestName} />
            </label>
            <dl className="checkout-totals">
              <div><dt>{t("Subtotal")}</dt><dd>{formatEgp(total, language)}</dd></div>
              {serviceFee > 0 ? <div><dt>{t("Service")}</dt><dd>{formatEgp(serviceFee, language)}</dd></div> : null}
              {vat > 0 ? <div><dt>{t("VAT")}</dt><dd>{formatEgp(vat, language)}</dd></div> : null}
              <div data-total="true"><dt>{t("To pay")}</dt><dd>{formatEgp(counterTotal, language)}</dd></div>
            </dl>
            <div aria-label={t("Payment method")} className="checkout-methods" role="group">
              {(settings?.allowed_payment_methods ?? ["cash", "card", "transfer", "other"]).map((option) => (
                <button aria-pressed={method === option} key={option} onClick={() => { setMethod(option); setError(null); }} type="button">
                  {t(option.charAt(0).toUpperCase() + option.slice(1))}
                </button>
              ))}
            </div>
            {method === "cash" ? (
              <div className="grid gap-2">
                <label className="grid gap-1">
                  <span className="ops-label">{t("Cash received")}</span>
                  <input className="ops-input checkout-amount" inputMode="decimal" min="0" onChange={(event) => setCashReceived(event.target.value)} step="0.01" type="number" value={cashReceived} />
                </label>
                {lines.length > 0 ? (
                  <div aria-label={t("Quick amounts")} className="flex flex-wrap gap-1.5" role="group">
                    {quickCash.map((value) => (
                      <button className="ops-button" data-size="sm" data-variant="secondary" key={value} onClick={() => setCashReceived(value.toFixed(2))} type="button">
                        {value === counterTotal ? t("Exact") : formatEgp(value, language)}
                      </button>
                    ))}
                  </div>
                ) : null}
                {changeDue > 0 ? <p className="checkout-change">{t("Give back {amount} change", { amount: formatEgp(changeDue, language) })}</p> : null}
              </div>
            ) : null}
          </>
        ) : (
          <>
            <div className="order-composer-total">
              <span>{t("Total")}</span>
              <span>{formatEgp(total, language)}</span>
            </div>
            <p className="ops-copy text-xs">{t("Added to the table's bill at checkout.")}</p>
          </>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            className="ops-button flex-1"
            data-variant="primary"
            disabled={sending || lines.length === 0 || (!selectedSession && !isCounter) || (isCounter && (!method || cashShort))}
            onClick={() => void send()}
            type="button"
          >
            {sending
              ? t("Sending…")
              : isCounter && lines.length > 0
                ? t("Take {amount} and send", { amount: formatEgp(counterTotal, language) })
                : itemCount > 0
                  ? t("Send order ({count})", { count: itemCount })
                  : t("Send order")}
          </button>
          {onCancel ? (
            <button className="ops-button" data-variant="secondary" onClick={onCancel} type="button">
              {t("Cancel")}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
